import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { PermissionDenied, SkeletonBlock } from '@/components/ui/Feedback';
import { hasAnyPerm } from '@/lib/permissions';
import { useMe, usePermissions, useSessionStatus } from './useAuth';

/**
 * Renders children only when the user holds `perm` (or any of `anyOf`). Hiding is cosmetic —
 * the API enforces every permission again.
 *   <Can perm="crm.customer.create"><Button>Tạo</Button></Can>
 *   <Can anyOf={['audit.read', 'audit.financial.read']} fallback={<PermissionDenied />}>…</Can>
 */
export function Can({ perm, anyOf, children, fallback = null }: { perm?: string; anyOf?: readonly string[]; children: ReactNode; fallback?: ReactNode }) {
  const perms = usePermissions();
  const required = perm ? [perm] : (anyOf ?? []);
  return <>{hasAnyPerm(perms, required) ? children : fallback}</>;
}

/** Route guard: anonymous → /login?next=…; waits for /me. */
export function RequireAuth() {
  const status = useSessionStatus();
  const location = useLocation();
  const me = useMe();
  if (status !== 'authenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (me.isLoading) {
    return (
      <div className="p-8">
        <SkeletonBlock lines={6} />
      </div>
    );
  }
  return <Outlet />;
}

/** Route-level permission guard: shows PermissionDenied (the route still exists, nothing is leaked). */
export function RequirePerm({ anyOf, children }: { anyOf?: readonly string[]; children: ReactNode }) {
  const perms = usePermissions();
  if (!hasAnyPerm(perms, anyOf)) return <PermissionDenied />;
  return <>{children}</>;
}
